// Offline outbox for the live audit. Answers and photos that can't reach the server
// (store Wi-Fi drops) are kept in IndexedDB and replayed in order when the connection
// returns. Each answer carries client_updated_at, so the server ignores a replayed
// answer that is older than one already saved from another device.
import { createStore, del, entries, set } from 'idb-keyval';
import { useSyncExternalStore } from 'react';
import { api, ApiError } from './api';

type ResponseJob = { kind: 'response'; auditId: string; itemKey: string; body: Record<string, unknown> };
type UploadJob = { kind: 'upload'; auditId: string; itemKey: string; blob: Blob; filename: string };
type Job = (ResponseJob | UploadJob) & { id: string; createdAt: string };

const store = createStore('bp-outbox', 'jobs');
let pending: Job[] = [];
let flushing = false;
let lastError: string | null = null;
const listeners = new Set<() => void>();
const onFlushed = new Set<(job: Job, result: unknown) => void>();

function emit() {
  snapshot = { count: pending.length, error: lastError, online: navigator.onLine };
  listeners.forEach((l) => l());
}
let snapshot = { count: 0, error: null as string | null, online: typeof navigator === 'undefined' ? true : navigator.onLine };

async function load() {
  const all = await entries<string, Job>(store);
  pending = all.map(([, j]) => j).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  emit();
}

async function enqueue(job: ResponseJob | UploadJob): Promise<Job> {
  const full: Job = { ...job, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
  // Only the newest pending answer per item matters.
  if (job.kind === 'response') {
    for (const p of pending.filter((p) => p.kind === 'response' && p.auditId === job.auditId && p.itemKey === job.itemKey)) {
      await del(p.id, store);
    }
    pending = pending.filter((p) => !(p.kind === 'response' && p.auditId === job.auditId && p.itemKey === job.itemKey));
  }
  await set(full.id, full, store);
  pending.push(full);
  emit();
  return full;
}

async function run(job: Job): Promise<unknown> {
  if (job.kind === 'response') {
    return api.put(`/api/audits/${job.auditId}/responses/${job.itemKey}`, { ...job.body, client_updated_at: job.createdAt });
  }
  return api.upload({ audit_id: job.auditId, item_key: job.itemKey }, job.blob, job.filename);
}

export async function flush(): Promise<void> {
  if (flushing || pending.length === 0) return;
  flushing = true;
  try {
    while (pending.length) {
      const job = pending[0];
      try {
        const result = await run(job);
        onFlushed.forEach((f) => f(job, result));
        lastError = null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 0) break; // still offline; try later
        // The server refused it (e.g. audit got locked). Drop it and surface the reason.
        lastError = e instanceof Error ? e.message : 'A saved change could not be synced.';
      }
      await del(job.id, store);
      pending.shift();
      emit();
    }
  } finally {
    flushing = false;
    emit();
  }
}

// Try the request now; if the network is down, queue it and resolve with null.
export async function sendOrQueue<T>(job: ResponseJob | UploadJob): Promise<T | null> {
  if (pending.some((p) => p.auditId === job.auditId)) {
    // Keep ordering: if this audit already has queued work, queue behind it.
    await enqueue(job);
    void flush();
    return null;
  }
  try {
    return (await run({ ...job, id: '', createdAt: new Date().toISOString() })) as T;
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) {
      await enqueue(job);
      return null;
    }
    throw e;
  }
}

export function pendingFor(auditId: string): Job[] {
  return pending.filter((p) => p.auditId === auditId);
}

export function onJobFlushed(fn: (job: Job, result: unknown) => void): () => void {
  onFlushed.add(fn);
  return () => {
    onFlushed.delete(fn);
  };
}

export function useOutbox() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => snapshot,
  );
}

if (typeof window !== 'undefined') {
  void load().then(flush);
  window.addEventListener('online', () => {
    emit();
    void flush();
  });
  window.addEventListener('offline', emit);
  setInterval(() => void flush(), 15_000);
}
